import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { TextField } from "@cmsgov/ds-medicare-gov";
import H1 from "../common/components/h1";
import Div from "../common/components/div";
import { SubmitButton } from "./buttons/Buttons";
import { HelpFooter } from "./shared-components/HelpFooter";
import { SmartErrorAlert } from "../common/components/SmartErrorAlert";
import { validateOtp } from "./validators/mfaSchema";
import { useMfaInlineError, useMfaPageView } from "../common/hooks/use.analytics";
import useLoginApi, { LoginResponse, MfaSendCodeResponse, SlsLoginErr, UpdateField } from "../common/hooks/useLoginApi";
import translate from "../common/functions/translate";
import { useHistory } from "react-router";
import { pushWithParams } from "../common/functions/pushWithParams";
import { GetAnotherCode } from "./shared-components/GetAnotherCode";
import { SecurityCodeMethod } from "./hooks/useMfaApi";
import { MfaSendSuccessAlert } from "./shared-components/MfaSendSuccessAlert";
import { usePageTitle } from "../common/hooks/usePageTitle";

interface ConfirmYourMethodProps {
    type: SecurityCodeMethod
    value: string
    username: string
    password: string
    id: string // MFA method id, used for /mfa/send
}

export const LoginConfirmYourMethod = (props: ConfirmYourMethodProps) => {
    const { type, value, username, password, id } = props;
    const [securityCode, setSecurityCode] = useState('');
    const [fieldErrorText, setFieldErrorText] = useState('');
    const [showFieldError, setShowFieldError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<AxiosResponse<LoginResponse> | AxiosResponse<MfaSendCodeResponse> | null>(null);
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const [loginFailCount, setLoginFailCount] = useState(0);
    const [isResend, setIsResend] = useState(false);

    usePageTitle(translate('confirm_it_is_you'));
    useMfaPageView('login', 'enter text security code');
    const loginApi = useLoginApi();
    const history = useHistory();
    const inputLabel = translate("security_code");
    const inlineError = useMfaInlineError(inputLabel);

    useEffect(() => {
        const errorKeys = validateOtp(securityCode);
        setFieldErrorText(errorKeys ? translate(errorKeys[0]) : '');
    }, [securityCode]);

    const redirectWithErrCode = (error: SlsLoginErr, updateFields: UpdateField[] = []) => {
        pushWithParams(history, '/login', { addParams: { error }, state: { updateFields } });
    }

    /** Redirect user to the login page and show temporary locked error */
    const redirectAndShowAccountLocked = () => {
        redirectWithErrCode('SSO-1');
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setShowInfoAlert(false);
        setShowFieldError(true);
        if (fieldErrorText || isLoading) {
            if (fieldErrorText) {
                inlineError.validationError(fieldErrorText);
            }
            return;
        }
        setIsLoading(true);
        const mfaLoginResponse = await loginApi.login(username, password, securityCode);
        setApiResponse(mfaLoginResponse);
        if (!mfaLoginResponse) {
            setIsLoading(false);
            return;
        }
        if (mfaLoginResponse.status === 200) {
            loginApi.loginSuccess(mfaLoginResponse, 'mfa');
            return;
        }
        setIsLoading(false);
        const errorCode = mfaLoginResponse.data.code;

        const incrementFailCount = () => {
            if (loginFailCount === 4) { // 5th failure
                redirectAndShowAccountLocked();
                return;
            }
            setLoginFailCount(prev => prev + 1);
        };
        switch (errorCode) {
            case 'SSO-1':
                redirectAndShowAccountLocked();
                return;
            case 'SSO-4':
            case 'SSO-6':
                redirectWithErrCode(errorCode);
                return;
            case 'SSO-13':
                redirectWithErrCode(errorCode, mfaLoginResponse.data.mustUpdate);
                return;
            case 'MFA_EXPIRED_OTP':
                incrementFailCount();
                const expiredErr = translate('code_expired');
                setFieldErrorText(expiredErr);
                inlineError.serverError(expiredErr, 'MFA_EXPIRED_OTP');
                break;
            case 'MFA_INVALID_OTP':
                incrementFailCount();
                const invalidErr = translate('invalid_code');
                setFieldErrorText(invalidErr);
                inlineError.serverError(invalidErr, 'MFA_INVALID_OTP');
                break;
            default:
                // other errors handled by <SmartErrorAlert />
        }
    }

    async function getAnotherCode() {
        setShowInfoAlert(false);
        const resendResponse = await loginApi.mfaSend(id);
        setApiResponse(resendResponse);
        if (resendResponse?.status === 204) {
            setIsResend(true);
            setShowInfoAlert(true);
        }
    }

    const headingTitle = translate("enter_your_security_code_to_login");
    return (
        <Div isContainer paddingy={1} paddingyMD={4}>
            <form onSubmit={onSubmit} method="POST">
                <Div isRow>
                    <Div cols={12}>
                        <H1>
                            {translate("confirm_it_is_you")}
                        </H1>
                        <Div isRow>
                            <Div cols={12} colsMD={8}>
                                {showInfoAlert && <MfaSendSuccessAlert isResend={isResend} type={type} value={value} />}
                            </Div>
                        </Div>
                        <SmartErrorAlert
                            name={"MfaLogin"}
                            fieldRef={null}
                            apiResponse={apiResponse}
                            conversion={null}
                            invalidFields={null}
                            onInvalidField={() => {}}
                            loginFailCount={loginFailCount}
                            parentHeading={headingTitle}
                        />
                        <h2 className={ showInfoAlert ? "ds-u-margin-bottom--3 ds-text-heading--xl ds-u-margin-top--0" : "ds-u-margin-bottom--3 ds-text-heading--xl ds-u-margin-top--3"}>
                            {headingTitle}
                        </h2>
                        <TextField
                            autoComplete="one-time-code"
                            fieldClassName="ds-u-font-size--xl"
                            labelClassName="ds-u-margin-top--0"
                            className="ds-u-margin-bottom--3"
                            aria-invalid={!!(showFieldError && fieldErrorText)}
                            errorMessage={showFieldError ? fieldErrorText : null}
                            inputMode="numeric"
                            label={inputLabel}
                            maxLength={6}
                            name={"securityCode"}
                            onChange={(event) => setSecurityCode(event.currentTarget.value)}
                            onBlur={() => setShowFieldError(true)}
                            pattern="[0-9]*"
                            size={"small"}
                            value={securityCode}
                        />
                    </Div>
                </Div>
                <GetAnotherCode
                    type={type}
                    onClick={getAnotherCode}
                    setShowInfoAlert={setShowInfoAlert}
                    mfaAnalyticsHeading={headingTitle}
                />
                <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                    text: translate('login'),
                    parentComponentHeading: headingTitle,
                }}>
                    {translate('login')}
                </SubmitButton>
                <Div className="ds-u-margin-top--7">
                    <HelpFooter title={translate(`cannot_access_your_${type}`)} />
                </Div>
            </form>
        </Div>
    );
}